import { useMemo } from "react";
import { getValueFromVariablesByFixedId } from "../../../utils/common";

/**
 * Custom hook to process user profile fields from userDetails object
 *
 * Takes a userDetails object with UserDetails and VisibleInCard properties
 * and returns processed fields array ready for display in UserProfileCard
 * Applies sorting based on CardSorting order if available
 *
 * @param {Object} userDetails - Object containing UserDetails and VisibleInCard
 * @param {Object} userDetails.UserDetails - User details object containing variables array
 * @param {Array} userDetails.UserDetails.variables - Array of user's custom variable values
 * @param {Array} userDetails.VisibleInCard - Array of fields visible in card configuration
 * @param {Object} userDetails.SortingOrder - Object containing sorting configurations
 * @param {string} userDetails.SortingOrder.CardSorting - Comma-separated string of field IDs for sorting
 * @returns {Array} Array of processed and sorted field objects with name, value, and id properties
 *
 * @author Sarthak Arora
 */
export const useProcessedFields = (userDetails) => {
  const processedFields = useMemo(() => {
    if (!userDetails?.VisibleInCard || !userDetails?.UserDetails?.variables) {
      return [];
    }

    const fields = userDetails.VisibleInCard.filter((item) => item?.name !== "Job Role").map((visibleField) => {
      let displayValue = "";

      if (visibleField.isSpecialVariable) {
        const fixedId = visibleField.fixedVariableId;
        const fieldName = visibleField.name || "";

        const isManager = fixedId === 7 || fieldName === "Manager Name";
        const isDepartment = fixedId === 3 || fieldName === "Department";
        const isOfficeLocation = fixedId === 8 || fieldName === "Office Location";
        const isJobRole = fixedId === 6 || fieldName === "Job Role";

        if (isManager) {
          const managerNameFromVars = userDetails.UserDetails.variables ? getValueFromVariablesByFixedId(userDetails.UserDetails.variables, 7) : null;
          displayValue = managerNameFromVars || userDetails.UserDetails.ManagerName || "Not selected";
        } else if (isDepartment) {
          const departmentFromVars = userDetails.UserDetails.variables ? getValueFromVariablesByFixedId(userDetails.UserDetails.variables, 3) : null;
          displayValue = departmentFromVars || userDetails.UserDetails.Department || "Not selected";
        } else if (isOfficeLocation) {
          const officeLocationFromVars = userDetails.UserDetails.variables
            ? getValueFromVariablesByFixedId(userDetails.UserDetails.variables, 8)
            : null;
          displayValue = officeLocationFromVars || userDetails.UserDetails.OfficeLocation || "Not selected";
        } else if (isJobRole) {
          const jobRoleFromVars = userDetails.UserDetails.variables ? getValueFromVariablesByFixedId(userDetails.UserDetails.variables, 6) : null;
          displayValue = jobRoleFromVars || userDetails.UserDetails.JobRole || "Not selected";
        } else {
          displayValue = "Not selected";
        }
      } else {
        const variable = userDetails.UserDetails.variables.find((v) => v.variableItemId === visibleField.variableItemId);
        if (variable) {
          if (visibleField.variableType === "dropdown" && !variable.attributes?.options) {
            variable.attributes = visibleField.attributes;
          }
          // If it's a dropdown type with options, find the label
          if (variable.attributes?.options && Array.isArray(variable.attributes.options)) {
            const selectedOption = variable.attributes.options.find((option) => Number(option.value) === Number(variable.value));
            displayValue = selectedOption?.label || "Not selected";
          } else {
            // For other types, use the value directly
            displayValue = variable.value?.toString() || "Not selected";
          }
        } else {
          // Field exists in VisibleInCard but user doesn't have this variable
          displayValue = "Not selected";
        }
      }

      return {
        name: visibleField.name,
        value: displayValue,
        id: visibleField._id || visibleField.variableItemId,
        originalField: visibleField, // Keep reference for sorting
      };
    });

    const cardSorting = userDetails?.SortingOrder?.CardSorting;
    if (!cardSorting) {
      return fields.map(({ originalField, ...field }) => field);
    }

    const sortingIds = cardSorting.includes(",") ? cardSorting.split(",").map((id) => id.trim()).filter(Boolean) : [];

    const sortedFields = [...fields].sort((a, b) => {
      const aIndex = sortingIds.indexOf(a.id);
      const bIndex = sortingIds.indexOf(b.id);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      if (aIndex !== -1) return -1;

      if (bIndex !== -1) return 1;

      return 0;
    });

    return sortedFields.map(({ originalField, ...field }) => field);
  }, [userDetails?.VisibleInCard, userDetails?.UserDetails?.variables, userDetails?.SortingOrder?.CardSorting]);

  return processedFields;
};
